import { db } from "../../config/firebase";
import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";

const toMillis = (value) => {
  if (!value) return value;
  if (typeof value === "number") return value;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return value;
};

const normalizeCard = (card) => {
  if (!card) return card;
  const createdAt = card.createdAt || card.createDate;
  return {
    ...card,
    createdAt: toMillis(createdAt),
    updatedAt: toMillis(card.updatedAt),
    createDate: toMillis(card.createDate),
  };
};

const buildServiceError = (error, fallbackMessage) => {
  return {
    message: error?.message || fallbackMessage || "Beklenmeyen bir hata oluştu.",
    code: error?.code || "UNKNOWN_ERROR",
  };
};

const listDigitalCards = async () => {
  try {
    const colRef = collection(db, "digitalcards");
    const querySnapshot = await getDocs(colRef);
    const cards = querySnapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return normalizeCard({ ...data, id: docSnap.id });
    });
    return cards.sort((a, b) => {
      const aTime = a.createdAt || a.createDate || a.activateDate || 0;
      const bTime = b.createdAt || b.createDate || b.activateDate || 0;
      return bTime - aTime;
    });
  } catch (error) {
    throw buildServiceError(error, "Kartvizitler yüklenemedi.");
  }
};

const createDigitalCard = async ({ fullName, email, phone, title, company, isActive }) => {
  try {
    const payload = {
      fullName: fullName || "",
      email: (email || "").toLowerCase(),
      phone: phone || "",
      title: title || "",
      company: company || "",
      isActive: typeof isActive === "boolean" ? isActive : true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const colRef = collection(db, "digitalcards");
    const docRef = await addDoc(colRef, payload);
    const docSnap = await getDoc(docRef);
    return normalizeCard({ ...docSnap.data(), id: docSnap.id });
  } catch (error) {
    throw buildServiceError(error, "Kartvizit oluşturulamadı.");
  }
};

const updateDigitalCard = async (id, data) => {
  try {
    const docRef = doc(db, "digitalcards", id);
    await updateDoc(docRef, { ...data, updatedAt: Timestamp.now() });
    const docSnap = await getDoc(docRef);
    return normalizeCard({ ...docSnap.data(), id: docSnap.id });
  } catch (error) {
    throw buildServiceError(error, "Kartvizit güncellenemedi.");
  }
};

const updateDigitalCardActive = async (id, isActive) => {
  try {
    const docRef = doc(db, "digitalcards", id);
    await updateDoc(docRef, { isActive, updatedAt: Timestamp.now() });
    const docSnap = await getDoc(docRef);
    return normalizeCard({ ...docSnap.data(), id: docSnap.id });
  } catch (error) {
    throw buildServiceError(error, "Durum güncellenemedi.");
  }
};

const deleteDigitalCard = async (id) => {
  try {
    const docRef = doc(db, "digitalcards", id);
    await deleteDoc(docRef);
    return { id };
  } catch (error) {
    throw buildServiceError(error, "Kartvizit silinemedi.");
  }
};

const getDigitalCardImage = async (cardId) => {
  try {
    const photoRef = doc(db, "digitalcards", cardId, "imagebase64", "photo");
    const snap = await getDoc(photoRef);
    if (!snap.exists()) return null;
    const data = snap.data() || {};
    return data.image || null;
  } catch (error) {
    throw buildServiceError(error, "Kartvizit görseli alınamadı.");
  }
};

const updateDigitalCardImage = async (cardId, imageBase64) => {
  try {
    const photoRef = doc(db, "digitalcards", cardId, "imagebase64", "photo");
    await setDoc(photoRef, { image: imageBase64 || "" }, { merge: true });
    return { id: cardId, image: imageBase64 || "" };
  } catch (error) {
    throw buildServiceError(error, "Kartvizit görseli güncellenemedi.");
  }
};

const digitalCardsService = {
  listDigitalCards,
  createDigitalCard,
  updateDigitalCard,
  updateDigitalCardActive,
  deleteDigitalCard,
  getDigitalCardImage,
  updateDigitalCardImage,
};

export default digitalCardsService;
